import React, { useMemo, memo } from "react";
import type { BiddingInfo } from "../../../types";
import { BidMinPriceDetails } from "./BiddingMinPriceDetails";
import { useTruncatedTooltip } from "../../common/TruncatedTooltip";
import Big from "big.js";
import { BiddingMinPriceSettingForm } from "./BiddingMinPriceInputForm";
// 一覧行用Props
interface BiddingMinPriceSettingItemRowProps {
  item: BiddingInfo;
  isSelected: boolean;
  onToggleSelect: () => void;
  isSaving: boolean;
  onSubmit: (data: {
    itemId: string;
    branchNumber: number;
    bidMinPrice: number;
  }) => void;
}

// # 一覧の各行を表すコンポーネント ---
export const BiddingMinPriceSettingItemRow: React.FC<
  BiddingMinPriceSettingItemRowProps
> = ({ item, isSelected, onToggleSelect, isSaving, onSubmit }) => {
  //----------------
  // Memo定義
  //----------------
  const displayInfo = useMemo(() => {
    try {
      // 1. 各値を Big オブジェクト化
      const c1 = new Big(item.capacity1 || 0);
      const c2 = new Big(item.capacity2 || 0);
      const q1 = new Big(item.quantity1 || 0);
      const q2 = new Big(item.quantity2 || 0);
      const bQ1 = new Big(item.bidQuantity1 || 0);
      const bQ2 = new Big(item.bidQuantity2 || 0);

      // 2. 10進数で正確に計算
      // totalCapacity = (c1 * q1) + (c2 * q2)
      const total = c1.times(q1).plus(c2.times(q2));

      // bidCapacity = (c1 * bQ1) + (c2 * bQ2)
      const bid = c1.times(bQ1).plus(c2.times(bQ2));

      return {
        totalCapacity: total.toNumber(),
        bidCapacity: bid.toNumber(),
      };
    } catch (e) {
      return { totalCapacity: 0, bidCapacity: 0 };
    }
    // 3. 依存配列に bidQuantity も追加（これらが変わった時も再計算が必要なため）
  }, [
    item.capacity1,
    item.quantity1,
    item.capacity2,
    item.quantity2,
    item.bidQuantity1,
    item.bidQuantity2,
  ]);

  //----------------
  // その他定数定義
  //----------------
  const rowClasses = "hover:bg-slate-50";
  const { tooltipProps, TooltipElement } = useTruncatedTooltip();

  //----------------
  // レンダリング処理
  //----------------
  return (
    <>
      {/* 全体のコンテナ */}
      <div
        className={`mt-2 lg:mt-4 border-b lg:pb-0 border-slate-800 lg:border lg:rounded-lg lg:shadow-sm overflow-hidden bg-white ${isSelected ? "ring-2 ring-indigo-500 rounded-lg" : ""}`}
      >
        {/* --- クリック可能な行ヘッダー --- */}
        <div
          className={`p-2 lg:p-4 ${rowClasses} transition-colors cursor-pointer relative`}
          onClick={onToggleSelect}
        >
          <div className="flex flex-col sm:flex-row sm:items-start">
            {/* --- 表示内容エリア --- */}
            <div className="flex-1 grid grid-cols-6 lg:grid-cols-13 gap-x-2 gap-y-1 sm:gap-x-4 items-baseline lg:items-start pr-8 sm:pr-0">
              {/* --- 入札番号 --- */}
              <div className="col-span-2 order-1 lg:order-none lg:col-span-1 min-w-[3rem]">
                <p className="hidden lg:block text-base text-slate-500">
                  入札番号
                </p>
                <p className="text-base lg:text-lg font-bold text-left">
                  {item.biddingNumber}
                </p>
              </div>

              {/* 茶期・茶種・品種 (PCのみ) */}
              <div className="hidden lg:block lg:col-span-1 lg:order-none">
                <p className="text-base text-slate-500">茶期</p>
                <p {...tooltipProps} className="text-lg font-bold truncate">
                  {item.teaSeasonName}
                </p>
              </div>
              <div className="hidden lg:block lg:col-span-1 lg:order-none">
                <p className="text-base text-slate-500">茶種</p>
                <p {...tooltipProps} className="text-lg font-bold truncate">
                  {item.teaTypeName}
                </p>
              </div>
              <div className="hidden lg:block lg:col-span-1 lg:order-none">
                <p className="text-base text-slate-500">品種名</p>
                <p {...tooltipProps} className="text-lg font-bold truncate">
                  {item.varietyName}
                </p>
              </div>

              {/* --- 生産者 --- */}
              <div className="col-span-3 order-4 lg:col-span-2 lg:order-none min-w-0 flex flex-col items-start lg:mt-0">
                <p className="hidden lg:block text-base text-slate-500">
                  生産者
                </p>
                <p
                  {...tooltipProps}
                  className="text-base lg:text-lg lg:font-bold truncate max-w-full lg:w-fit"
                >
                  {item.farmerName}
                </p>
              </div>

              {/* --- 売手 --- */}
              <div className="col-span-3 order-2 lg:col-span-2 lg:order-none min-w-0 flex flex-col items-start lg:mt-0">
                <p className="hidden lg:block text-base text-slate-500">売手</p>
                <p
                  {...tooltipProps}
                  className="text-base lg:text-lg lg:font-bold truncate max-w-full lg:w-fit"
                >
                  {item.sellerName}
                </p>
              </div>

              {/* --- 荷印 --- */}
              <div className="col-span-2 order-3 lg:order-none lg:col-span-2 text-left lg:pl-4">
                <p className="hidden lg:block text-base text-slate-500">荷印</p>
                <p
                  {...tooltipProps}
                  className="text-base lg:text-lg text-left lg:font-bold truncate ml-2.5 lg:ml-0"
                >
                  {item.brand}
                </p>
              </div>

              {/* --- 総量 --- */}
              <div className="hidden lg:block lg:col-span-1 lg:order-none min-w-0 lg:pl-2">
                <p className="hidden lg:block text-base text-slate-500 whitespace-nowrap text-left">
                  総量(kg)
                </p>
                <p
                  {...tooltipProps}
                  className="text-base lg:text-lg font-bold truncate text-left"
                >
                  {displayInfo.totalCapacity}
                  <span className="lg:hidden text-sm font-normal ml-0.5 text-slate-600">
                    kg
                  </span>
                </p>
              </div>

              {/* --- 最低落札値 PC右端 --- */}
              <div className="hidden col-span-3 order-6 lg:col-span-2 lg:order-none lg:flex flex-col lg:items-end items-start mt-2 lg:mt-0">
                <p className="text-base text-slate-500">最低落札値(円)</p>
                <p className="text-slate-900 text-lg lg:text-xl font-black">
                  {item.bidMinPrice.toLocaleString()}
                  <span className="lg:hidden text-sm font-normal ml-0.5 text-slate-600">
                    円
                  </span>
                </p>
              </div>
            </div>

            {/* モバイル用：最低落札値 */}
            <div className="lg:hidden w-full mt-3">
              <div className="flex items-baseline justify-center w-full bg-slate-100 rounded-lg py-2">
                {Number(item.bidMinPrice) > 0 ? (
                  <>
                    <span className="text-xl font-black text-slate-900 tracking-wider">
                      {Number(item.bidMinPrice).toLocaleString()}
                    </span>
                    <span className="text-sm font-normal text-slate-500 ml-1">
                      円
                    </span>
                  </>
                ) : (
                  <span className="text-lg font-bold text-slate-900">
                    未設定
                  </span>
                )}
              </div>
            </div>

            {/* モバイル用: 隠れている項目の簡易表示 */}
            <div className="md:hidden py-2 text-base flex flex-wrap gap-x-3 pointer-events-none justify-left ml-2.5 lg:mt-2">
              <span>{item.teaSeasonName}</span>
              <span>/</span>
              <span>{item.teaTypeName}</span>
              <span>/</span>
              <span>{item.varietyName}</span>
              <span>/</span>
              <span>{displayInfo.totalCapacity}kg</span>
            </div>

            {/* --- モバイル用 展開アイコン --- */}
            <div className="absolute top-4 right-4 sm:hidden pointer-events-none">
              <svg
                className={`w-6 h-6 text-slate-400 transition-transform ${isSelected ? "rotate-180" : ""}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* --- 詳細情報・設定フォームエリア --- */}
        <div
          className={`transition-all duration-300 ease-in-out overflow-hidden ${isSelected ? "max-h-[1200px]" : "max-h-0"}`}
        >
          {isSelected && (
            <div className="border-t border-slate-200">
              {/* 詳細情報 */}
              <BidMinPriceDetails item={item} displayInfo={displayInfo} />
              {/* 最低落札値設定フォーム */}
              <BiddingMinPriceSettingForm
                item={item}
                onSubmit={onSubmit}
                isSaving={isSaving}
              />
            </div>
          )}
        </div>
      </div>
      {TooltipElement}
    </>
  );
};

// React.memo でラップ
export default memo(BiddingMinPriceSettingItemRow);
